import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import { api, Clawback, User } from '../lib/api';
import { fmtCurrency, fmtMonth } from '../lib/fmt';
import { downloadCsv } from '../lib/csv';
import {
  SectionLabel, Card, CardHeader, Table, Th, Tr, Td, Badge,
  ActionButton, Spinner, EmptyState, Select, UserName, SortableTh,
} from '../components/ui';
import { useTableSort } from '../hooks/useTableSort';

const MONTH_PATTERN = /^\d{4}-\d{2}$/;

export default function AdminClawbacksPage() {
  const { data: usersData } = useQuery(() => api.admin.users.list());
  const { data, isLoading, error, refetch } = useQuery(() => api.admin.clawbacks.list());
  const [showCreate, setShowCreate] = useState(false);

  const users = usersData?.users ?? [];
  const userById = useMemo(
    () => new Map(users.map((u) => [u.jira_user_id, u])),
    [users],
  );

  const clawbacks = data?.clawbacks ?? [];
  const { sorted: sortedClawbacks, sort } = useTableSort(
    clawbacks,
    {
      user:       (c) => userById.get(c.user_jira_id)?.display_name ?? c.user_jira_id,
      original:   (c) => c.original_attribution_month,
      work_item:  (c) => c.work_item_jira_issue_id,
      hours:      (c) => Number(c.written_off_billable_hours),
      amount:     (c) => Number(c.clawback_amount),
      applied:    (c) => c.applied_to_attribution_month ?? 'zzzz', // pending last
      notes:      (c) => c.notes ?? '',
      created:    (c) => c.created_at,
    },
    { key: 'created', dir: 'desc' },
  );

  async function onDelete(cb: Clawback) {
    if (cb.applied_to_attribution_month) {
      alert('This clawback has already been applied and cannot be deleted.');
      return;
    }
    if (!confirm(`Delete clawback of ${fmtCurrency(cb.clawback_amount)}?`)) return;
    try {
      await api.admin.clawbacks.delete(cb.id);
      refetch();
    } catch (e) {
      alert(`Delete failed: ${(e as Error).message}`);
    }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <SectionLabel>Clawbacks</SectionLabel>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
          <ActionButton
            onClick={() => {
              const rows: Array<Array<unknown>> = [[
                'User', 'Original period', 'Work item', 'Hours', 'Amount',
                'Applied to', 'Created by', 'Notes',
              ]];
              for (const cb of sortedClawbacks) {
                const u = userById.get(cb.user_jira_id);
                rows.push([
                  u?.display_name ?? cb.user_jira_id,
                  cb.original_attribution_month,
                  cb.work_item_jira_issue_id,
                  cb.written_off_billable_hours.toFixed(2),
                  cb.clawback_amount.toFixed(2),
                  cb.applied_to_attribution_month ?? 'unapplied',
                  cb.created_by_admin_user_id,
                  cb.notes ?? '',
                ]);
              }
              downloadCsv('clawbacks.csv', rows);
            }}
            variant="default"
            disabled={!clawbacks.length}
          >
            Export CSV
          </ActionButton>
          <ActionButton onClick={() => setShowCreate(true)} variant="primary">
            Record clawback
          </ActionButton>
        </span>
      </div>

      <div style={{ padding: '0 0 10px', fontSize: 12, color: 'var(--color-text-muted)' }}>
        Billable hours written off against a previously-paid bonus. The clawback amount is
        deducted from the employee's next earning period (carries forward if no bonus is
        earned). See spec §4.3.
      </div>

      <Card>
        <CardHeader>
          <span style={{ fontSize: 12, fontWeight: 500 }}>
            {clawbacks.length} clawback{clawbacks.length === 1 ? '' : 's'}
          </span>
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
            {clawbacks.filter((c) => !c.applied_to_attribution_month).length} unapplied
          </span>
        </CardHeader>
        {isLoading ? <Spinner /> : error ? <EmptyState message={error} /> : clawbacks.length === 0 ? (
          <EmptyState message="No clawbacks recorded." />
        ) : (
          <Table>
            <thead>
              <tr>
                <SortableTh columnKey="user"      sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>User</SortableTh>
                <SortableTh columnKey="original"  sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Original period</SortableTh>
                <SortableTh columnKey="work_item" sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Work item</SortableTh>
                <SortableTh columnKey="hours"     sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Hours</SortableTh>
                <SortableTh columnKey="amount"    sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort} right>Amount</SortableTh>
                <SortableTh columnKey="applied"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Applied to</SortableTh>
                <SortableTh columnKey="notes"     sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Notes</SortableTh>
                <SortableTh columnKey="created"   sortKey={sort.sortKey} dir={sort.dir} setSort={sort.setSort}>Created</SortableTh>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {sortedClawbacks.map((cb) => {
                const u = userById.get(cb.user_jira_id);
                return (
                  <Tr key={cb.id}>
                    <Td bold>
                      <UserName name={u?.display_name ?? cb.user_jira_id} active={u?.is_active ?? true} />
                    </Td>
                    <Td muted style={{ fontSize: 11 }}>{fmtMonth(cb.original_attribution_month)}</Td>
                    <Td muted style={{ fontSize: 11 }}>{cb.work_item_jira_issue_id}</Td>
                    <Td right>{Number(cb.written_off_billable_hours).toFixed(2)}</Td>
                    <Td right bold style={{ color: 'var(--color-red-text)' }}>
                      −{fmtCurrency(cb.clawback_amount)}
                    </Td>
                    <Td>
                      {cb.applied_to_attribution_month
                        ? <Badge variant="green">{fmtMonth(cb.applied_to_attribution_month)}</Badge>
                        : <Badge variant="amber">pending</Badge>}
                    </Td>
                    <Td muted style={{ fontSize: 11 }}>{cb.notes || '—'}</Td>
                    <Td muted style={{ fontSize: 11 }}>
                      {new Date(cb.created_at).toLocaleDateString()}
                    </Td>
                    <Td>
                      {!cb.applied_to_attribution_month && (
                        <ActionButton onClick={() => onDelete(cb)} variant="danger">
                          Delete
                        </ActionButton>
                      )}
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {showCreate && (
        <CreateClawbackModal
          users={users}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); refetch(); }}
        />
      )}
    </>
  );
}

function CreateClawbackModal({
  users, onClose, onCreated,
}: {
  users: User[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [userJiraId, setUserJiraId] = useState('');
  const [month, setMonth] = useState('');
  const [issueId, setIssueId] = useState('');
  const [hours, setHours] = useState('');
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hoursNum = parseFloat(hours);
  const canSubmit = !!userJiraId && MONTH_PATTERN.test(month) && !!issueId && hoursNum > 0;

  useEffect(() => {
    if (!canSubmit) { setPreview(null); return; }
    let cancelled = false;
    api.admin.clawbacks.preview({
      user_jira_id: userJiraId,
      original_attribution_month: month,
      work_item_jira_issue_id: issueId,
      written_off_billable_hours: hoursNum,
    }).then((r) => { if (!cancelled) setPreview(r.clawback_amount); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [userJiraId, month, issueId, hoursNum, canSubmit]);

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await api.admin.clawbacks.create({
        user_jira_id: userJiraId,
        original_attribution_month: month,
        work_item_jira_issue_id: issueId,
        written_off_billable_hours: hoursNum,
        notes: notes || undefined,
      });
      onCreated();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface)', borderRadius: 8, padding: 24, width: 480,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)', maxHeight: '90vh', overflow: 'auto',
        }}
      >
        <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 12 }}>Record clawback</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12 }}>
          <Label text="User">
            <Select value={userJiraId} onChange={setUserJiraId}>
              <option value="">— choose —</option>
              {users
                // Active first, inactive grouped below — but inactive users
                // must still be pickable since they may have historical data
                // eligible for clawback.
                .sort((a, b) => {
                  if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
                  return a.display_name.localeCompare(b.display_name);
                })
                .map((u) => (
                  <option key={u.jira_user_id} value={u.jira_user_id}>
                    {u.display_name}{u.is_active ? '' : ' (inactive)'}
                  </option>
                ))}
            </Select>
          </Label>
          <Label text="Original attribution month (YYYY-MM)">
            <input
              type="text"
              placeholder="2026-02"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={inputStyle}
            />
          </Label>
          <Label text="Work item (Jira issue ID or key)">
            <input
              type="text"
              placeholder="PROJ-42 or numeric id"
              value={issueId}
              onChange={(e) => setIssueId(e.target.value)}
              style={inputStyle}
            />
          </Label>
          <Label text="Written-off billable hours">
            <input
              type="number"
              step="0.25"
              min="0"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              style={inputStyle}
            />
          </Label>
          <Label text="Notes (reason for the write-off)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }}
            />
          </Label>
          {preview != null && (
            <div style={{ padding: 10, background: 'var(--color-bg)', borderRadius: 4, fontSize: 12 }}>
              Computed clawback amount:{' '}
              <strong style={{ color: 'var(--color-red-text)' }}>
                {fmtCurrency(preview)}
              </strong>
              <div style={{ color: 'var(--color-text-muted)', fontSize: 11, marginTop: 4 }}>
                = hours × billable rate × bonus multiplier at the time
              </div>
            </div>
          )}
          {error && (
            <div style={{ color: 'var(--color-red-text)', fontSize: 11 }}>{error}</div>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <ActionButton onClick={onClose} variant="default">Cancel</ActionButton>
          <ActionButton onClick={submit} variant="primary" disabled={!canSubmit || saving}>
            {saving ? 'Recording…' : 'Record clawback'}
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

function Label({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{text}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  fontSize: 12, padding: '6px 8px',
  border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
};
